import type { ComputedRef } from "vue";
import type { TableProps } from "../TableProps";
import type { TableHeaderCellProps } from "../TableHeaderCellProps";
import type { TableContext } from "../useTable.types";
import { SortDirection, TABLE_ROW_TYPE } from "../useTable.types";

type StateStore = TableContext["stateStore"];

const DIRECTION_CYCLE: Record<string, SortDirection | null> = {
  [SortDirection.None]: SortDirection.Ascending,
  [SortDirection.Ascending]: SortDirection.Descending,
  [SortDirection.Descending]: null,
};

export const getBodyRows = (
  bodyUid: ComputedRef<number | null>,
  stateStore: StateStore,
): Array<{ uid: number; element: HTMLElement }> => {
  if (!bodyUid.value) return [];
  const rowStates = stateStore.findChildrenByType(bodyUid.value, TABLE_ROW_TYPE);
  const allRowElements = document.querySelectorAll<HTMLElement>("e-table-row");
  const results: Array<{ uid: number; element: HTMLElement }> = [];
  for (const rowState of rowStates) {
    if (rowState.uid == null) continue;
    const uidStr = String(rowState.uid);
    for (const el of allRowElements) {
      if (el.dataset.uid === uidStr) {
        results.push({ uid: rowState.uid, element: el });
        break;
      }
    }
  }
  return results;
};

export const getCellTextContent = (rowElement: HTMLElement, columnIndex: number): string => {
  const cell = rowElement.querySelectorAll<HTMLElement>("e-table-cell")[columnIndex];
  if (!cell) return "";
  return cell.getAttribute("value") ?? cell.textContent?.trim() ?? "";
};

export const parseSortValue = (value: string): number | string => {
  if (value === "") return value;
  const cleaned = value.replaceAll(/[$,€£¥\s]/g, "");
  const num = Number.parseFloat(cleaned);
  if (!Number.isNaN(num) && !Number.isNaN(Number(cleaned))) return num;
  return value.toLowerCase();
};

export const compareSortValues = (a: number | string, b: number | string): number => {
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a).localeCompare(String(b));
};

export const applySorting = (
  tableProps: ComputedRef<TableProps | undefined>,
  bodyUid: ComputedRef<number | null>,
  stateStore: StateStore,
): void => {
  const sortCol = tableProps.value?.sortColumn;
  const sortDir = tableProps.value?.sortDirection;
  if (sortCol == null || sortDir === SortDirection.None) return;

  const rows = getBodyRows(bodyUid, stateStore);
  if (rows.length === 0) return;

  const bodyElement = rows[0].element.parentElement;
  if (!bodyElement) return;

  const sortedRows = rows.toSorted((a, b) => {
    const comparison = compareSortValues(
      parseSortValue(getCellTextContent(a.element, sortCol)),
      parseSortValue(getCellTextContent(b.element, sortCol)),
    );
    return sortDir === SortDirection.Ascending ? comparison : -comparison;
  });

  for (const row of sortedRows) bodyElement.append(row.element);
};

export const resetOtherHeaders = (
  headerCells: ComputedRef<Array<{ uid?: number | null }>>,
  currentUid: number,
  stateStore: StateStore,
): void => {
  for (const header of headerCells.value) {
    if (header.uid !== currentUid && header.uid != null) {
      stateStore.setValue<TableHeaderCellProps>(header.uid, "sortDirection", SortDirection.None);
    }
  }
};

export const resolveNextDirection = (
  currentDir: SortDirection,
  isSameColumn: boolean,
  explicitDirection?: SortDirection,
): SortDirection | null => {
  if (explicitDirection) return explicitDirection;
  if (!isSameColumn) return SortDirection.Ascending;
  const next = DIRECTION_CYCLE[currentDir];
  return next === undefined ? SortDirection.Ascending : next;
};
